import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { PendingSpinner } from "@/components/ui/pending-indicator";

const alertVariants = cva(
  "rounded-xl border px-4 py-3 text-sm animate-[fadeIn_220ms_ease-out]",
  {
    variants: {
      variant: {
        default:
          "border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-foreground)]",
        destructive:
          "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/8 text-[var(--color-destructive)]",
        muted:
          "border-[var(--color-border)] bg-[var(--color-background)]/80 text-[var(--color-muted)]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

/** Quiet in-flight copy → show spinner (plan/27). */
const PENDING_COPY =
  /^(Loading|Searching|Signing|Saving|Syncing|Posting|Working|Fetching|Submitting)\b/i;

function pendingLabel(children: React.ReactNode): string | null {
  if (typeof children === "string") {
    const t = children.trim();
    return PENDING_COPY.test(t) ? t : null;
  }
  if (Array.isArray(children) && children.length === 1) {
    return pendingLabel(children[0]);
  }
  return null;
}

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {}

export function Alert({ className, variant, children, ...props }: AlertProps) {
  const label = variant === "muted" ? pendingLabel(children) : null;
  return (
    <div
      role="status"
      aria-busy={label ? true : undefined}
      className={cn(
        alertVariants({ variant }),
        label && "inline-flex w-full items-center gap-2",
        className,
      )}
      {...props}
    >
      {label ? (
        <>
          <PendingSpinner className="text-[var(--color-primary)]" />
          <span>{label}</span>
        </>
      ) : (
        children
      )}
    </div>
  );
}
